/** Storefront and checkout API share these delivery rules and quote DTOs. */
export interface CheckoutQuoteLine {
  variantId?: string;
  title: string;
  quantity: number;
  unitAmountGrosze: number;
  deliveryIndex: number;
  attributes?: Array<{ key: string; value: string }>;
}
export interface CheckoutQuote {
  version: 1;
  currency: 'pln';
  lines: CheckoutQuoteLine[];
  itemsAmountGrosze: number;
  deliveryAmountGrosze: number;
  deliveryAmountsGrosze: number[];
  amountGrosze: number;
}

/** Compare commercial terms, regardless of UI row order or translated titles. */
export function checkoutQuoteFingerprint(quote: Pick<CheckoutQuote, 'lines' | 'deliveryAmountsGrosze'>): string {
  const lines = quote.lines.map(line => JSON.stringify([line.variantId || `addon:${line.title}`, line.quantity, line.unitAmountGrosze, line.deliveryIndex])).sort();
  return JSON.stringify([lines, quote.deliveryAmountsGrosze]);
}

export interface DeliveryChargeIdentity { address: string; date: string; slot: string }

/** Only a complete destination and its scheduled window identify a paid trip. */
export function deliveryChargeKey(delivery: DeliveryChargeIdentity): string | null {
  const address = delivery.address.normalize('NFC').trim().replace(/\s+/gu, ' ').toLowerCase();
  if (!address) return null; // A pickup never has a courier charge.
  // Preserve street numbers, apartment numbers and punctuation. Formatting alone
  // may match; a different apartment must never become the same destination.
  const rawSlot = delivery.slot.normalize('NFC').trim();
  const match = /^(\d{1,2})(?::(\d{2}))?\s*[-–—]\s*(\d{1,2})(?::(\d{2}))?$/.exec(rawSlot);
  const validTime = match && Number(match[1]) < 24 && Number(match[3]) < 24
    && Number(match[2] ?? 0) < 60 && Number(match[4] ?? 0) < 60;
  const slot = validTime
    ? `${match[1].padStart(2, '0')}:${match[2] ?? '00'}-${match[3].padStart(2, '0')}:${match[4] ?? '00'}`
    : rawSlot;
  // The checkout's existing schedule validator checks ISO dates and available
  // slots separately; this helper never broadens accepted delivery schedules.
  return JSON.stringify([address, delivery.date, slot]);
}

/** Retain all gift groups, but charge one trip per address/date/window. */
export function allocateDeliveryChargesGrosze(
  deliveries: readonly DeliveryChargeIdentity[],
  feesGrosze: readonly number[],
): number[] {
  if (deliveries.length !== feesGrosze.length
    || feesGrosze.some(fee => !Number.isSafeInteger(fee) || fee < 0)) {
    throw new Error('Invalid delivery charge amounts.');
  }
  const amounts = deliveries.map(() => 0);
  const firstIndexByTrip = new Map<string, number>();
  deliveries.forEach((delivery, index) => {
    const key = deliveryChargeKey(delivery);
    if (key === null) return;
    const firstIndex = firstIndexByTrip.get(key) ?? index;
    firstIndexByTrip.set(key, firstIndex);
    // A stale preview or a differing route quote must not understate the one
    // verified trip. Keep the highest individual fee, never their sum.
    amounts[firstIndex] = Math.max(amounts[firstIndex], feesGrosze[index]);
  });
  return amounts;
}

export const SAME_DAY_ORDER_CUTOFF = { weekday: 18, sunday: 15 } as const;
export interface DeliverySlot { value: string; label: string; cutoffHour: number; surcharge?: number }
export const WEEKDAY_DELIVERY_SLOTS: DeliverySlot[] = [
  { value: '9-19', label: '9:00 – 19:00', cutoffHour: 16 },
  { value: '9-11', label: '9:00 – 11:00', cutoffHour: 8 },
  { value: '11-13', label: '11:00 – 13:00', cutoffHour: 10 },
  { value: '13-15', label: '13:00 – 15:00', cutoffHour: 12 },
  { value: '15-17', label: '15:00 – 17:00', cutoffHour: 14 },
  { value: '17-19', label: '17:00 – 19:00', cutoffHour: 16 },
  { value: '19-21', label: '19:00 – 21:00', cutoffHour: SAME_DAY_ORDER_CUTOFF.weekday, surcharge: 20 },
];
export const SUNDAY_DELIVERY_SLOTS: DeliverySlot[] = [
  { value: '10-16', label: '10:00 – 16:00', cutoffHour: SAME_DAY_ORDER_CUTOFF.sunday },
  { value: '10-13', label: '10:00 – 13:00', cutoffHour: 9 },
  { value: '13-16', label: '13:00 – 16:00', cutoffHour: SAME_DAY_ORDER_CUTOFF.sunday },
];
export const WEEKDAY_PICKUP_SLOTS = WEEKDAY_DELIVERY_SLOTS.filter(slot => !['9-19', '19-21'].includes(slot.value))
  .map(slot => ({ ...slot, value: `p-${slot.value}`, cutoffHour: slot.value === '17-19' ? SAME_DAY_ORDER_CUTOFF.weekday : slot.cutoffHour }));
export const SUNDAY_PICKUP_SLOTS = SUNDAY_DELIVERY_SLOTS.filter(slot => slot.value !== '10-16')
  .map(slot => ({ ...slot, value: `p-${slot.value}` }));

/** Formats current and legacy saved windows consistently in receipts and UI. */
export function getDeliverySlotLabel(slot: string): string {
  const match = /^(?:p-)?(\d{1,2})-(\d{1,2})$/.exec(slot);
  return match ? `${match[1]}:00–${match[2]}:00` : slot;
}

function easterSunday(year: number): Date {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  return new Date(year, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);
}
function addDays(date: Date, days: number): Date { const next = new Date(date); next.setDate(next.getDate() + days); return next; }
export function getPolishHolidays(year: number): Date[] {
  const easter = easterSunday(year);
  return [[0, 1], [0, 6], [4, 1], [4, 3], [7, 15], [10, 1], [10, 11], [11, 25], [11, 26]]
    .map(([month, day]) => new Date(year, month, day))
    .concat([easter, addDays(easter, 1), addDays(easter, 49), addDays(easter, 60)]);
}
export function isPolishHoliday(date: Date): boolean {
  return getPolishHolidays(date.getFullYear()).some(day => day.getMonth() === date.getMonth() && day.getDate() === date.getDate());
}
export function skipHolidays(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  let date = new Date(year, month - 1, day);
  while (isPolishHoliday(date)) date = addDays(date, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function warsawCheckoutTime(now = new Date()): { date: string; hour: number } {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  const part = (type: string) => parts.find(value => value.type === type)!.value;
  return { date: `${part('year')}-${part('month')}-${part('day')}`, hour: Number(part('hour')) };
}
export function deliveryScheduleError(date: string, slot: string, pickup: boolean, now = new Date()): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Wybierz prawidłową datę dostawy.';
  const parsed = new Date(`${date}T12:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return 'Wybierz prawidłową datę dostawy.';
  const current = warsawCheckoutTime(now);
  if (date < current.date) return 'Wybrany termin już minął. Wybierz nową datę.';
  const [year, month, day] = date.split('-').map(Number);
  if (isPolishHoliday(new Date(year, month - 1, day))) return 'W wybranym dniu nie realizujemy zamówień. Wybierz inną datę.';
  const sunday = parsed.getUTCDay() === 0;
  const slots = pickup ? (sunday ? SUNDAY_PICKUP_SLOTS : WEEKDAY_PICKUP_SLOTS) : (sunday ? SUNDAY_DELIVERY_SLOTS : WEEKDAY_DELIVERY_SLOTS);
  const selected = slots.find(value => value.value === slot);
  if (!selected) return 'Wybierz dostępny przedział godzin dla tej daty.';
  if (date === current.date && current.hour >= selected.cutoffHour) return 'Minął czas zamówienia na tę godzinę. Wybierz nowy termin.';
  return null;
}
